// accentTheme.js — runtime accent retint (hue + intensity). design-uplift E2.
//
// Cursor-parity "Hue + Intensity": let the user retint the whole accent system
// at runtime. Apolo's accent lives in ~16 CSS vars in tokens.css that all embed
// blue-500 (rgba(59,130,246,…)) — accent fill, hover, muted/ring/border, the
// glow stack, the now-line, and two brand gradients. A partial override would
// look "half-themed", so this rebuilds the WHOLE set from one hue.
//
// Two knobs:
//   • hue        0–360 — which color the accent is (default 217 ≈ blue-500)
//   • intensity  0–100 — saturation scale (0 = neutral/gray accent, 100 = full)
//
// Byte-identical default: when {hue, intensity} === DEFAULT, applyAccent CLEARS
// the inline vars so tokens.css's own values win — the app renders exactly as
// before for anyone who never opens the picker.
//
// WCAG: lightness is FIXED at each theme's default accent L (dark 60% / light
// 53%) — the hand-tuned value that already passes the tokens.contrast gate for
// blue and stays balanced for accent-as-text AND white-text-on-accent. Only hue
// and saturation move, so contrast tracks the default for the common hues. The
// static contrast test (defaults, unchanged) stays green by construction.

export const ACCENT_KEY = 'apolo:accent';
export const DEFAULT_ACCENT = Object.freeze({ hue: 217, intensity: 100 });

// Every accent-derived CSS var this module owns. Listed so applyAccent can
// CLEAR exactly these (and no more) when returning to the default.
export const ACCENT_VAR_NAMES = Object.freeze([
  '--apolo-accent',
  '--apolo-accentHover',
  '--apolo-accentMuted',
  '--apolo-accentRing',
  '--apolo-accentBorder',
  '--apolo-glow-accent',
  '--apolo-glow-accent-tight',
  '--apolo-glow-accent-wide',
  '--apolo-glow-accent-ring',
  '--apolo-logo-glow',
  '--apolo-tab-active-border',
  '--apolo-cta-shadow',
  '--apolo-now-line',
  '--apolo-now-line-glow',
  '--apolo-gradient-tab-active',
  '--apolo-cta-gradient',
]);

// Per-theme base of the DEFAULT accent.
const THEME_BASE = {
  dark: { sat: 91, light: 60, hoverLight: 68 },
  light: { sat: 83, light: 53, hoverLight: 45 },
};

// Max relative luminance the accent fill may reach per theme — set a hair above
// the DEFAULT accent's own luminance (hsl(217,91,60)=0.2393 dark / hsl(217,83,53)
// =0.1792 light) so the default hue is a NO-OP (never clamps) while any brighter
// custom hue is darkened down to this floor. Result: white-on-accent (and
// accent-as-text) is at least as contrast-safe as the shipped blue for EVERY hue
// (HSL fixed-L alone is not — yellow/cyan at L60 are near-white, ~1.2:1).
const LUM_MAX = { dark: 0.2394, light: 0.1793 };

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// WCAG relative luminance of an HSL color (h 0-360, s/l 0-100).
function srgbToLin(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}
function hslToRgb(h, s, l) {
  const sN = s / 100;
  const lN = l / 100;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return lN - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [255 * f(0), 255 * f(8), 255 * f(4)];
}
function relLuminance(h, s, l) {
  const [r, g, b] = hslToRgb(h, s, l);
  return 0.2126 * srgbToLin(r) + 0.7152 * srgbToLin(g) + 0.0722 * srgbToLin(b);
}
// Lower lightness until the accent's luminance is within the theme cap. Hues
// already at/under the cap (blue, purple, …) are unchanged → default preserved.
function contrastSafeLight(h, s, baseL, target) {
  let L = baseL;
  while (L > 18 && relLuminance(h, s, L) > target) L -= 1;
  return L;
}

export function clampHue(h) {
  if (h == null) return DEFAULT_ACCENT.hue; // null/undefined → default (Number(null)===0 would lie)
  const n = Number(h);
  if (!Number.isFinite(n)) return DEFAULT_ACCENT.hue;
  return ((Math.round(n) % 360) + 360) % 360;
}

export function clampIntensity(i) {
  if (i == null) return DEFAULT_ACCENT.intensity; // null/undefined → default, not 0
  const n = Number(i);
  if (!Number.isFinite(n)) return DEFAULT_ACCENT.intensity;
  return clamp(Math.round(n), 0, 100);
}

export function normalizeAccent(a) {
  return { hue: clampHue(a?.hue), intensity: clampIntensity(a?.intensity) };
}

export function isDefaultAccent(a) {
  const n = normalizeAccent(a);
  return n.hue === DEFAULT_ACCENT.hue && n.intensity === DEFAULT_ACCENT.intensity;
}

export function readAccent() {
  try {
    const raw = JSON.parse(localStorage.getItem(ACCENT_KEY));
    if (raw && (raw.hue != null || raw.intensity != null)) return normalizeAccent(raw);
  } catch {
    /* private mode / corrupt — fall through to default */
  }
  return { ...DEFAULT_ACCENT };
}

export function writeAccent(a) {
  const next = normalizeAccent(a);
  try {
    localStorage.setItem(ACCENT_KEY, JSON.stringify(next));
  } catch {
    /* private mode — keep going so the in-memory accent still applies */
  }
  return next;
}

const hsl = (h, s, l, a) =>
  a == null ? `hsl(${h} ${s}% ${l}%)` : `hsl(${h} ${s}% ${l}% / ${a})`;

/**
 * Build the full accent CSS-var override map for a hue/intensity + theme.
 * @returns {Record<string,string>} var name → value (always all ACCENT_VAR_NAMES)
 */
export function buildAccentVars(accent, theme = 'dark') {
  const { hue, intensity } = normalizeAccent(accent);
  const base = THEME_BASE[theme] || THEME_BASE.dark;
  const s = Math.round(base.sat * (intensity / 100));
  // Hue-aware lightness: darken bright hues to the theme's luminance cap so the
  // accent stays contrast-safe for any hue (default blue is at the cap → no-op).
  const l = contrastSafeLight(hue, s, base.light, LUM_MAX[theme] ?? LUM_MAX.dark);
  // Keep hover's lightness offset relative to the (possibly clamped) accent.
  const hoverL = clamp(l + (base.hoverLight - base.light), 18, 92);
  const hueB = (hue + 30) % 360; // 2nd CTA-gradient stop (indigo-ish at default)
  return {
    '--apolo-accent': hsl(hue, s, l),
    '--apolo-accentHover': hsl(hue, s, hoverL),
    '--apolo-accentMuted': hsl(hue, s, l, 0.12),
    '--apolo-accentRing': hsl(hue, s, l, 0.25),
    '--apolo-accentBorder': hsl(hue, s, l, 0.3),
    '--apolo-glow-accent': `0 0 8px ${hsl(hue, s, l, 0.25)}`,
    '--apolo-glow-accent-tight': `0 0 18px -4px ${hsl(hue, s, l, 0.35)}`,
    '--apolo-glow-accent-wide': `0 8px 24px -8px ${hsl(hue, s, l, 0.55)}`,
    '--apolo-glow-accent-ring': `0 0 0 1px ${hsl(hue, s, l, 0.14)}`,
    '--apolo-logo-glow': `drop-shadow(0 0 8px ${hsl(hue, s, l, 0.25)})`,
    '--apolo-tab-active-border': hsl(hue, s, l, 0.45),
    '--apolo-cta-shadow': `0 0 0 1px ${hsl(hue, s, l, 0.4)}, 0 8px 24px -6px ${hsl(hue, s, l, 0.6)}`,
    '--apolo-now-line': `linear-gradient(to bottom, transparent, ${hsl(hue, s, l)} 25%, ${hsl(hue, s, l)} 75%, transparent)`,
    '--apolo-now-line-glow': `0 0 8px ${hsl(hue, s, l, 0.6)}`,
    // Brand gradients keep their warm (yellow) final stop; the cool stops retint.
    '--apolo-gradient-tab-active': `linear-gradient(135deg, ${hsl(hue, s, l, 0.22)} 0%, ${hsl(hue, s, l, 0.06)} 60%, rgba(250, 204, 21, 0.05) 100%)`,
    '--apolo-cta-gradient': `linear-gradient(135deg, ${hsl(hue, s, l)} 0%, ${hsl(hueB, s, l)} 50%, rgba(250, 204, 21, 0.9) 100%)`,
  };
}

/**
 * WCAG contrast of white over the (clamped) accent fill — the worst-case use
 * (accent buttons paint white text on bg-apolo-accent). Exposed for the a11y
 * test that samples the whole hue wheel. Higher = more readable.
 */
export function whiteOnAccentContrast(accent, theme = 'dark') {
  const { hue, intensity } = normalizeAccent(accent);
  const base = THEME_BASE[theme] || THEME_BASE.dark;
  const s = Math.round(base.sat * (intensity / 100));
  const l = contrastSafeLight(hue, s, base.light, LUM_MAX[theme] ?? LUM_MAX.dark);
  const lum = relLuminance(hue, s, l);
  return 1.05 / (lum + 0.05);
}

/**
 * Apply (or clear) the accent override on the document root for the given theme.
 * Default accent → clear all vars (tokens.css wins, byte-identical). Custom →
 * set every accent var so the retint cascades to all ~184 consumers at once.
 * @param {{hue:number,intensity:number}} accent
 * @param {'dark'|'light'} theme  resolved theme
 * @param {HTMLElement} [root]    defaults to document.documentElement
 */
export function applyAccent(accent, theme = 'dark', root) {
  const el = root || (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return;
  if (isDefaultAccent(accent)) {
    for (const name of ACCENT_VAR_NAMES) el.style.removeProperty(name);
    return;
  }
  const vars = buildAccentVars(accent, theme);
  for (const name of ACCENT_VAR_NAMES) el.style.setProperty(name, vars[name]);
}
